import { supabase } from '../lib/supabase';

export const adminService = {
  // Get all users with their company and subscription info
  async getAllUsers(filters = {}) {
    try {
      let query = supabase?.from('user_profiles')?.select(`
          *,
          company_employees!inner(
            company_id,
            position,
            department,
            status,
            companies(name, status)
          ),
          subscriptions(tier, status, current_period_end)
        `);

      // Apply filters
      if (filters?.role) {
        query = query?.eq('role', filters?.role);
      }
      if (filters?.status) {
        query = query?.eq('company_employees.status', filters?.status);
      }
      if (filters?.search) {
        query = query?.or(`full_name.ilike.%${filters?.search}%,email.ilike.%${filters?.search}%`);
      }

      const { data, error } = await query?.order('created_at', { ascending: false });

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  // Update user role and permissions
  async updateUserRole(userId, role) {
    try {
      const { error } = await supabase?.from('user_profiles')?.update({ role, updated_at: new Date()?.toISOString() })?.eq('id', userId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error updating user role:', error);
      throw error;
    }
  },

  // Update user status
  async updateUserStatus(userId, isActive) {
    try {
      const { error } = await supabase?.from('user_profiles')?.update({ is_active: isActive, updated_at: new Date()?.toISOString() })?.eq('id', userId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error updating user status:', error);
      throw error;
    }
  },

  // Bulk invite users
  async bulkInviteUsers(invitations) {
    try {
      const results = [];
      
      for (const invitation of invitations) {
        try {
          // In production, this would send invitation emails
          // For now, just log the invitation
          results?.push({
            email: invitation?.email,
            status: 'sent',
            role: invitation?.role
          });
        } catch (error) {
          results?.push({
            email: invitation?.email,
            status: 'failed',
            error: error?.message
          });
        }
      }

      return results;
    } catch (error) {
      console.error('Error bulk inviting users:', error);
      throw error;
    }
  },

  // Get subscription analytics
  async getSubscriptionAnalytics() {
    try {
      const { data, error } = await supabase?.from('subscriptions')?.select('tier, status');

      if (error) throw error;

      // Calculate analytics
      const analytics = {
        total: data?.length,
        byTier: {},
        byStatus: {},
        activeSubscriptions: 0
      };

      data?.forEach(sub => {
        // Count by tier
        analytics.byTier[sub.tier] = (analytics?.byTier?.[sub?.tier] || 0) + 1;
        
        // Count by status
        analytics.byStatus[sub.status] = (analytics?.byStatus?.[sub?.status] || 0) + 1;
        
        // Count active
        if (sub?.status === 'active' || sub?.status === 'trial') {
          analytics.activeSubscriptions++;
        }
      });

      return analytics;
    } catch (error) {
      console.error('Error getting subscription analytics:', error);
      throw error;
    }
  },

  // Get company settings
  async getCompanySettings(companyId) {
    try {
      const { data, error } = await supabase?.from('companies')?.select('*')?.eq('id', companyId)?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error getting company settings:', error);
      throw error;
    }
  },

  // Update company settings
  async updateCompanySettings(companyId, settings) {
    try {
      const { error } = await supabase?.from('companies')?.update({
          settings: settings,
          updated_at: new Date()?.toISOString()
        })?.eq('id', companyId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error updating company settings:', error);
      throw error;
    }
  },

  // Get user activity logs
  async getUserActivityLogs(userId, limit = 50) {
    try {
      const { data, error } = await supabase?.from('application_logs')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false })?.limit(limit);

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error getting activity logs:', error);
      throw error;
    }
  }
};